import { WorkModel } from "../../factories/works/models/work.model";
import {
  ClientOverviewRow,
  PaymentRow,
  PortalReportData,
  ProjectedCopy,
  RequestReportRow,
} from "../../slices/ReportDataSlice";
import {
  clientKey,
  DateRange,
  daysBetween,
  entryClient,
  entryHours,
  entryPerson,
  entryValue,
  InvoiceLike,
  isBillableEntry,
  isOverdue,
  monthKey,
  monthsOf,
  startOfDay,
  statusGroup,
  TimeEntry,
} from "./reports.utils";

/**
 * Rules of the nine reports added 2026-11-05 (owner-approved mock-ups). Everything is
 * computed in the browser from find-work / filter-time / filter-invoice and the read-only
 * `api/report-data` endpoints, like the other reports. Pure functions (tests in
 * moreReports.utils.test.ts).
 */

const DAY = 24 * 60 * 60 * 1000;
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const FULL_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const toDate = (value?: string | Date | null) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "2026-11-15" as that local calendar day (not UTC midnight). */
export const localDay = (value: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return toDate(value);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

/** Monday of the week the date is in (weeks run Monday to Sunday). */
export const weekStart = (date: Date) => {
  const day = startOfDay(date);
  const shift = (day.getDay() + 6) % 7;
  day.setDate(day.getDate() - shift);
  return day;
};

const addDays = (date: Date, days: number) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

/** "Nov 9" for the first week of a month or the first bar, "16" for the others (like the mock-up). */
export const weekLabels = (starts: Date[]) =>
  starts.map((start, i) =>
    i === 0 || start.getMonth() !== starts[i - 1].getMonth()
      ? `${MONTHS[start.getMonth()]} ${start.getDate()}`
      : String(start.getDate()),
  );

export const shortDate = (value?: string | Date | null) => {
  const date = value instanceof Date ? value : value ? localDay(value) : null;
  if (!date) return "—";
  return `${MONTHS[date.getMonth()]} ${date.getDate()}`;
};

/** "Today", "Yesterday", "12 days ago", "3 months ago". */
export const agoText = (
  value?: string | Date | null,
  today: Date = new Date(),
) => {
  const date = toDate(value);
  if (!date) return "";
  const days = Math.max(0, daysBetween(date, today));
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 60) return `${days} days ago`;
  const months = monthsBetween(date, today);
  return months < 24
    ? `${months} months ago`
    : `${Math.floor(months / 12)} years ago`;
};

/** Whole months from `from` to `to` (a month is counted once its day has passed). */
export const monthsBetween = (from: Date, to: Date) => {
  let months =
    (to.getFullYear() - from.getFullYear()) * 12 +
    (to.getMonth() - from.getMonth());
  if (to.getDate() < from.getDate()) months -= 1;
  return Math.max(0, months);
};

// ---------------------------------------------------------------- 1. Client profitability

export type ProfitRow = {
  key: string;
  customerId: string;
  name: string;
  hours: number;
  value: number;
  billed: number;
  paid: number;
  /** billed ÷ value in %, null when there's no value of time. */
  realization: number | null;
};

/**
 * Per client: billable hours and their value (hours x each person's hourly rate, like
 * filter-time), what was billed (invoice totals) and paid (total − balance) in the range.
 */
export const profitabilityByClient = (
  time: TimeEntry[],
  invoices: InvoiceLike[],
): ProfitRow[] => {
  const rows = new Map<string, ProfitRow>();
  const row = (key: string, customerId: string, name: string) => {
    const found = rows.get(key);
    if (found) return found;
    const created: ProfitRow = {
      key,
      customerId,
      name,
      hours: 0,
      value: 0,
      billed: 0,
      paid: 0,
      realization: null,
    };
    rows.set(key, created);
    return created;
  };
  time.filter(isBillableEntry).forEach((entry) => {
    const r = row(clientKey(entry), entry.customerId || "", entryClient(entry));
    r.hours += entryHours(entry);
    r.value += entryValue(entry);
  });
  invoices.forEach((invoice) => {
    const r = row(
      invoice.customerId || `name:${invoice.customerName}`,
      invoice.customerId || "",
      invoice.customerName || "No client",
    );
    r.billed += invoice.total || 0;
    r.paid += Math.max(0, (invoice.total || 0) - (invoice.balance || 0));
  });
  return Array.from(rows.values())
    .map((r) => ({ ...r, realization: realization(r.billed, r.value) }))
    .sort(
      (a, b) =>
        (a.realization ?? 999) - (b.realization ?? 999) || b.value - a.value,
    );
};

export const realization = (billed: number, value: number) =>
  value > 0 ? Math.round((billed / value) * 100) : null;

/** Below 70% red, below 85% orange, else green (owner's mock-up). */
export const realizationTone = (pct: number | null) =>
  pct === null ? "none" : pct < 70 ? "red" : pct < 85 ? "amber" : "green";

export const TONE_TEXT = {
  red: "#B42318",
  amber: "#B54708",
  green: "#147A4B",
  none: "#5B6472",
} as const;
export const TONE_BAR = {
  red: "#D92D20",
  amber: "#F79009",
  green: "#12B76A",
  none: "#DCE2EA",
} as const;

// ---------------------------------------------------------------- 2. Team utilization

export type UtilRow = {
  id: string;
  name: string;
  color?: string;
  billable: number;
  nonBillable: number;
  total: number;
  /** billable ÷ all tracked time, %. */
  pct: number;
};

export const utilizationByPerson = (time: TimeEntry[]): UtilRow[] => {
  const rows = new Map<string, UtilRow>();
  time.forEach((entry) => {
    const id = entry.assignedUserId || "unknown";
    const r =
      rows.get(id) ??
      ({
        id,
        name: entryPerson(entry),
        color: entry.assignedUser?.userProfile?.color || undefined,
        billable: 0,
        nonBillable: 0,
        total: 0,
        pct: 0,
      } as UtilRow);
    const hours = entryHours(entry);
    if (isBillableEntry(entry)) r.billable += hours;
    else r.nonBillable += hours;
    r.total += hours;
    rows.set(id, r);
  });
  return Array.from(rows.values())
    .filter((r) => r.total > 0)
    .map((r) => ({ ...r, pct: Math.round((r.billable / r.total) * 100) }))
    .sort((a, b) => b.pct - a.pct || b.total - a.total);
};

export const TARGET_OPTIONS = [50, 55, 60, 65, 70, 75, 80, 85, 90, 95].map(
  (n) => ({ value: String(n), label: `${n}%` }),
);

/** More than 10 points under the target (mock-up's "Under target"). */
export const underTarget = (rows: UtilRow[], target: number) =>
  rows.filter((r) => r.pct < target - 10);

// ---------------------------------------------------------------- 3. Busy season forecast

export type ForecastMeasure = "items" | "hours";
export type ForecastItem = {
  due: Date;
  hours: number;
  /** Assignee ids; empty = unassigned. */
  people: string[];
  projected: boolean;
};

export type ForecastWeek = {
  start: Date;
  label: string;
  created: number;
  projected: number;
  total: number;
};

/** Open work already created + the recurring copies still to come, as one list. */
export const forecastItems = (
  works: WorkModel[],
  projected: ProjectedCopy[],
): ForecastItem[] => [
  ...works
    .filter((w) => statusGroup(w) !== "Completed")
    .map((w) => ({
      due: toDate(w.dueDate) as Date,
      hours: w.budgetedTime || 0,
      people: (w.assigneeList || [])
        .map((a) => a.assignedUserId)
        .filter(Boolean),
      projected: false,
    }))
    .filter((x) => !!x.due),
  ...projected
    .map((p) => ({
      due: localDay(p.dueDate) as Date,
      hours: p.budgetHours || 0,
      people: p.assigneeIds || [],
      projected: true,
    }))
    .filter((x) => !!x.due),
];

/** The projected copies the report's filters keep (team member / client / work type). */
export const filterProjected = (
  projected: ProjectedCopy[],
  filters: { userIds: string[]; clientIds: string[]; workTypeIds: string[] },
) =>
  projected.filter(
    (p) =>
      (!filters.userIds.length ||
        p.assigneeIds.some((id) => filters.userIds.includes(id)) ||
        !p.assigneeIds.length) &&
      (!filters.clientIds.length || filters.clientIds.includes(p.customerId)) &&
      (!filters.workTypeIds.length ||
        filters.workTypeIds.includes(p.workTypeId || "")),
  );

/** Work already past its due date (due before today): shown as its own "Overdue" bar. */
export const forecastOverdue = (
  items: ForecastItem[],
  measure: ForecastMeasure,
  today: Date = new Date(),
) => {
  const todayStart = startOfDay(today);
  const out = { created: 0, projected: 0, total: 0, count: 0 };
  items.forEach((item) => {
    if (item.due >= todayStart) return;
    const amount = measure === "hours" ? item.hours : 1;
    if (item.projected) out.projected += amount;
    else out.created += amount;
    out.count += 1;
  });
  out.created = Math.round(out.created * 10) / 10;
  out.projected = Math.round(out.projected * 10) / 10;
  out.total = Math.round((out.created + out.projected) * 10) / 10;
  return out;
};

/**
 * Weeks from this week to `months` months ahead. Overdue work (due before today) is
 * left out: it has its own bar (`forecastOverdue`, owner 2026-11-05).
 */
export const forecastWeeks = (
  items: ForecastItem[],
  months: number,
  measure: ForecastMeasure,
  today: Date = new Date(),
): ForecastWeek[] => {
  const first = weekStart(today);
  const horizon = new Date(
    today.getFullYear(),
    today.getMonth() + months,
    today.getDate(),
    23,
    59,
    59,
  );
  const starts: Date[] = [];
  for (let s = first; s <= horizon; s = addDays(s, 7)) starts.push(s);
  const labels = weekLabels(starts);
  const weeks = starts.map((start, i) => ({
    start,
    label: labels[i],
    created: 0,
    projected: 0,
    total: 0,
  }));
  const todayStart = startOfDay(today);
  items.forEach((item) => {
    if (item.due > horizon || item.due < todayStart) return;
    const index = Math.floor(
      (weekStart(item.due).getTime() - first.getTime() + DAY / 2) / (7 * DAY),
    );
    const week = weeks[index];
    if (!week) return;
    const amount = measure === "hours" ? item.hours : 1;
    if (item.projected) week.projected += amount;
    else week.created += amount;
  });
  return weeks.map((w) => ({
    ...w,
    created: Math.round(w.created * 10) / 10,
    projected: Math.round(w.projected * 10) / 10,
    total: Math.round((w.created + w.projected) * 10) / 10,
  }));
};

export type ForecastCell = { items: number; hours: number };
export type ForecastGridRow = {
  id: string;
  name: string;
  cells: ForecastCell[];
  total: number;
};

/** The months shown in the grid: this month and the next `months - 1`. */
export const forecastMonths = (months: number, today: Date = new Date()) =>
  Array.from({ length: months }, (_, i) => {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    return { key: monthKey(d), label: FULL_MONTHS[d.getMonth()], start: d };
  });

/** Month x person: items · budget hours due (work with two people counts for both). */
export const forecastGrid = (
  items: ForecastItem[],
  months: number,
  names: Record<string, string>,
  today: Date = new Date(),
): ForecastGridRow[] => {
  const cols = forecastMonths(months, today);
  const index = new Map(cols.map((c, i) => [c.key, i]));
  const thisMonth = monthKey(
    new Date(today.getFullYear(), today.getMonth(), 1),
  );
  const rows = new Map<string, ForecastGridRow>();
  items.forEach((item) => {
    const key =
      item.due < new Date(today.getFullYear(), today.getMonth(), 1)
        ? thisMonth
        : monthKey(item.due);
    const col = index.get(key);
    if (col === undefined) return;
    (item.people.length ? item.people : [""]).forEach((id) => {
      const row =
        rows.get(id) ??
        ({
          id,
          name: id ? names[id] || "Former team member" : "Unassigned",
          cells: cols.map(() => ({ items: 0, hours: 0 })),
          total: 0,
        } as ForecastGridRow);
      row.cells[col].items += 1;
      row.cells[col].hours += item.hours;
      row.total += 1;
      rows.set(id, row);
    });
  });
  return Array.from(rows.values()).sort(
    (a, b) => Number(a.id === "") - Number(b.id === "") || b.total - a.total,
  );
};

/** Cell shade like the mock-up (darker = more due). */
export const gridShade = (items: number, max: number) => {
  const r = max > 0 ? items / max : 0;
  if (r >= 0.8) return { bg: "#6B4EFF", fg: "#FFFFFF" };
  if (r >= 0.5) return { bg: "#A493FF", fg: "#1F2433" };
  if (r >= 0.25) return { bg: "#D9D2FF", fg: "#1F2433" };
  return { bg: "#F3F0FF", fg: "#1F2433" };
};

// ---------------------------------------------------------------- 4. Client requests

export const requestStats = (
  lists: RequestReportRow[],
  today: Date = new Date(),
) => {
  const clients = new Set(lists.map((l) => l.client.customerId));
  const asked = lists.reduce((s, l) => s + l.itemsTotal, 0);
  const done = lists.reduce((s, l) => s + l.itemsDone, 0);
  return {
    open: lists.length,
    clients: clients.size,
    missing: asked - done,
    asked,
    overdue: lists.filter((l) => isRequestOverdue(l, today)).length,
  };
};

export const isRequestOverdue = (
  list: Pick<RequestReportRow, "dueDate">,
  today: Date = new Date(),
) => {
  const due = list.dueDate ? localDay(list.dueDate) : null;
  return !!due && due < startOfDay(today);
};

/** "3 of 6", or "Off" when the list has no automatic reminders. */
export const remindersText = (list: RequestReportRow) =>
  list.remindersOff && !list.remindersSent
    ? "Off"
    : `${list.remindersSent} of ${list.maxReminders}`;

// ---------------------------------------------------------------- 5. Payments received

export const paymentMonths = (
  payments: PaymentRow[],
  invoices: InvoiceLike[],
  range: DateRange,
) => {
  const dates = [
    ...payments.map((p) => toDate(p.date)),
    ...invoices.map((i) => toDate(i.invoiceDate)),
  ].filter((d): d is Date => !!d);
  const months = monthsOf(range, dates).map((m) => ({
    ...m,
    billed: 0,
    received: 0,
  }));
  const at = new Map(months.map((m, i) => [m.key, i]));
  payments.forEach((p) => {
    const d = toDate(p.date);
    const i = d ? at.get(monthKey(d)) : undefined;
    if (i !== undefined) months[i].received += p.amount || 0;
  });
  invoices.forEach((inv) => {
    const d = toDate(inv.invoiceDate);
    const i = d ? at.get(monthKey(d)) : undefined;
    if (i !== undefined) months[i].billed += inv.total || 0;
  });
  return months;
};

export const paymentsByMethod = (payments: PaymentRow[]) => {
  const total = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const map = new Map<
    string,
    { name: string; amount: number; count: number }
  >();
  payments.forEach((p) => {
    const name = p.method || "Not set";
    const row = map.get(name) ?? { name, amount: 0, count: 0 };
    row.amount += p.amount || 0;
    row.count += 1;
    map.set(name, row);
  });
  return Array.from(map.values())
    .map((r) => ({
      ...r,
      pct: total > 0 ? Math.round((r.amount / total) * 100) : 0,
    }))
    .sort((a, b) => b.amount - a.amount);
};

export const paymentsByClient = (payments: PaymentRow[]) => {
  const map = new Map<
    string,
    {
      key: string;
      customerId: string;
      name: string;
      amount: number;
      count: number;
    }
  >();
  payments.forEach((p) => {
    const key = p.customerId || `name:${p.clientName}`;
    const row = map.get(key) ?? {
      key,
      customerId: p.customerId || "",
      name: p.clientName,
      amount: 0,
      count: 0,
    };
    row.amount += p.amount || 0;
    row.count += 1;
    map.set(key, row);
  });
  return Array.from(map.values()).sort((a, b) => b.amount - a.amount);
};

/** Average days from invoice to payment, weighted by the amount paid (null = none known). */
export const averageDaysToPay = (payments: PaymentRow[]) => {
  const known = payments.filter(
    (p) => p.daysToPay !== null && p.daysToPay !== undefined,
  );
  const weight = known.reduce((s, p) => s + Math.max(0, p.amount || 0), 0);
  if (!known.length) return null;
  const value =
    weight > 0
      ? known.reduce(
          (s, p) => s + Math.max(0, p.amount || 0) * (p.daysToPay as number),
          0,
        ) / weight
      : known.reduce((s, p) => s + (p.daysToPay as number), 0) / known.length;
  return Math.round(value);
};

/** The same range one year earlier (for "vs. last year"); null for All time. */
export const yearBefore = (range: DateRange): DateRange => {
  if (!range) return null;
  const shift = (d: Date) => {
    const x = new Date(d);
    x.setFullYear(x.getFullYear() - 1);
    return x;
  };
  return { from: shift(range.from), to: shift(range.to) };
};

/** "+12%" / "−4%" / null. */
export const changeText = (now: number, before: number) => {
  if (before <= 0) return null;
  const pct = Math.round(((now - before) / before) * 100);
  return `${pct >= 0 ? "+" : "−"}${Math.abs(pct)}%`;
};

// ---------------------------------------------------------------- 6. Work started vs. finished

export type ThroughputWeek = {
  start: Date;
  label: string;
  started: number;
  finished: number;
  openAtEnd: number;
};

const completedAt = (w: WorkModel) =>
  statusGroup(w) === "Completed" ? toDate(w.statusUpdatedAt) : null;

/** Open at a moment: started by then, and not completed by then. */
const openAt = (w: WorkModel, at: Date) => {
  const start = toDate(w.startDate);
  if (!start || start > at) return false;
  const done = completedAt(w);
  if (statusGroup(w) === "Completed" && !done) return false;
  return !done || done > at;
};

/**
 * The last `weeks` weeks (Monday to Sunday, this week last): started = start date in the
 * week, finished = set to Completed in the week (statusUpdatedAt, like the Completed report),
 * open work at the end of each week.
 */
export const throughputWeeks = (
  works: WorkModel[],
  weeks: number,
  today: Date = new Date(),
) => {
  const last = weekStart(today);
  const starts = Array.from({ length: weeks }, (_, i) =>
    addDays(last, -7 * (weeks - 1 - i)),
  );
  const labels = weekLabels(starts);
  const rows: ThroughputWeek[] = starts.map((start, i) => {
    const end = new Date(addDays(start, 7).getTime() - 1);
    const until = end > today ? today : end;
    return {
      start,
      label: labels[i],
      started: works.filter((w) => {
        const s = toDate(w.startDate);
        return !!s && s >= start && s <= until;
      }).length,
      finished: works.filter((w) => {
        const d = completedAt(w);
        return !!d && d >= start && d <= until;
      }).length,
      openAtEnd: works.filter((w) => openAt(w, until)).length,
    };
  });
  const before = new Date(starts[0].getTime() - 1);
  const finishedIn = works.filter((w) => {
    const d = completedAt(w);
    return !!d && d >= starts[0] && d <= today;
  });
  const durations = finishedIn
    .map((w) => {
      const s = toDate(w.startDate);
      const d = completedAt(w);
      return s && d ? Math.max(0, daysBetween(s, d)) : null;
    })
    .filter((n): n is number => n !== null);
  return {
    weeks: rows,
    started: rows.reduce((s, r) => s + r.started, 0),
    finished: rows.reduce((s, r) => s + r.finished, 0),
    openBefore: works.filter((w) => openAt(w, before)).length,
    openNow: rows.length ? rows[rows.length - 1].openAtEnd : 0,
    averageDays: durations.length
      ? Math.round(durations.reduce((s, n) => s + n, 0) / durations.length)
      : null,
  };
};

// ---------------------------------------------------------------- 7. Clients by owner / manager

export type OwnerMode = "owner" | "manager";
export type ClientWithWork = ClientOverviewRow & {
  open: number;
  overdue: number;
  balance: number;
};
export type OwnerRow = {
  id: string;
  name: string;
  color?: string | null;
  archived?: boolean;
  clients: number;
  open: number;
  overdue: number;
  balance: number;
};

/** Each client's open work, overdue work and unpaid balance. */
export const clientsWithWork = (
  clients: ClientOverviewRow[],
  works: WorkModel[],
  invoices: InvoiceLike[],
  today: Date = new Date(),
): ClientWithWork[] => {
  const open = new Map<string, { open: number; overdue: number }>();
  works
    .filter((w) => statusGroup(w) !== "Completed")
    .forEach((w) => {
      const r = open.get(w.customerId) ?? { open: 0, overdue: 0 };
      r.open += 1;
      if (isOverdue(w, today)) r.overdue += 1;
      open.set(w.customerId, r);
    });
  const balance = new Map<string, number>();
  invoices.forEach((i) => {
    if ((i.balance || 0) > 0.005)
      balance.set(i.customerId, (balance.get(i.customerId) || 0) + i.balance);
  });
  return clients.map((c) => ({
    ...c,
    open: open.get(c.customerId)?.open || 0,
    overdue: open.get(c.customerId)?.overdue || 0,
    balance: balance.get(c.customerId) || 0,
  }));
};

export const NO_PERSON = "";

export const personOf = (client: ClientOverviewRow, mode: OwnerMode) =>
  mode === "owner" ? client.owner : client.manager;

export const ownerRows = (clients: ClientWithWork[], mode: OwnerMode) => {
  const rows = new Map<string, OwnerRow>();
  clients.forEach((c) => {
    const p = personOf(c, mode);
    const id = p?.id || NO_PERSON;
    const row =
      rows.get(id) ??
      ({
        id,
        name: p
          ? p.name + (p.archived ? " (archived)" : "")
          : mode === "owner"
          ? "No client owner"
          : "No client manager",
        color: p?.color,
        archived: p?.archived,
        clients: 0,
        open: 0,
        overdue: 0,
        balance: 0,
      } as OwnerRow);
    row.clients += 1;
    row.open += c.open;
    row.overdue += c.overdue;
    row.balance += c.balance;
    rows.set(id, row);
  });
  return Array.from(rows.values()).sort(
    (a, b) =>
      Number(a.id === NO_PERSON) - Number(b.id === NO_PERSON) ||
      b.clients - a.clients ||
      a.name.localeCompare(b.name),
  );
};

export const clientsOfPerson = (
  clients: ClientWithWork[],
  mode: OwnerMode,
  personId: string,
) =>
  clients
    .filter((c) => (personOf(c, mode)?.id || NO_PERSON) === personId)
    .sort(
      (a, b) =>
        b.overdue - a.overdue ||
        b.open - a.open ||
        a.name.localeCompare(b.name),
    );

// ---------------------------------------------------------------- 8. Quiet clients

export const QUIET_OPTIONS = [
  { value: "3", label: "3 months" },
  { value: "6", label: "6 months" },
  { value: "12", label: "12 months" },
];

export type QuietRow = ClientOverviewRow & { quietMonths: number };

/** Active clients with nothing for at least `months` months, quietest first. */
export const quietClients = (
  clients: ClientOverviewRow[],
  months: number,
  today: Date = new Date(),
): QuietRow[] => {
  const cutoff = new Date(
    today.getFullYear(),
    today.getMonth() - months,
    today.getDate(),
  );
  return (
    clients
      .map((c) => {
        const last = toDate(c.lastActivityAt);
        return {
          ...c,
          quietMonths: last ? monthsBetween(last, today) : 999,
          last,
        };
      })
      .filter((c) => !c.last || c.last <= cutoff)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
      .map(({ last, ...rest }) => rest)
      .sort(
        (a, b) => b.quietMonths - a.quietMonths || a.name.localeCompare(b.name),
      )
  );
};

/** "Invoice · Apr 2025" (mock-up). */
export const lastActivityText = (c: ClientOverviewRow) => {
  const d = toDate(c.lastActivityAt);
  if (!d) return "Nothing recorded";
  return `${c.lastActivity || "Activity"} · ${
    MONTHS[d.getMonth()]
  } ${d.getFullYear()}`;
};

export const quietForText = (months: number) =>
  months >= 999
    ? "Never active"
    : months === 1
    ? "1 month"
    : `${months} months`;

/** Billed last year per client. */
export const billedByClient = (invoices: InvoiceLike[]) => {
  const map = new Map<string, number>();
  invoices.forEach((i) =>
    map.set(i.customerId, (map.get(i.customerId) || 0) + (i.total || 0)),
  );
  return map;
};

// ---------------------------------------------------------------- 9. Client portal and signatures

export const portalStats = (data: PortalReportData) => {
  const active = data.clients.filter((c) => c.portal === "active").length;
  const invited = data.clients.filter(
    (c) => c.portal === "invited" || c.portal === "expired",
  ).length;
  return {
    total: data.clients.length,
    active,
    invited,
    pct: data.clients.length
      ? Math.round((active / data.clients.length) * 100)
      : 0,
    uploads: data.clients.reduce((s, c) => s + c.uploadsLast90Days, 0),
    waiting: data.waiting.length,
    waitingLong: data.waiting.filter((w) => w.daysWaiting >= 14).length,
  };
};

export const PORTAL_STATUS_TEXT = {
  active: "Active",
  invited: "Invited",
  expired: "Invite expired",
  none: "Not invited",
} as const;

/** The line next to the status ("Uploaded 4 files · today", "Invite sent 9 days ago"). */
export const portalLine = (
  c: PortalReportData["clients"][number],
  today: Date = new Date(),
) => {
  if (c.portal === "active") {
    const parts: string[] = [];
    if (c.uploadsLast90Days)
      parts.push(
        `Uploaded ${c.uploadsLast90Days} file${
          c.uploadsLast90Days === 1 ? "" : "s"
        } · ${agoText(c.lastUploadAt, today).toLowerCase()}`,
      );
    else if (c.lastSignedAt)
      parts.push(`Signed a document · ${shortDate(new Date(c.lastSignedAt))}`);
    else if (c.acceptedAt)
      parts.push(`Set up ${agoText(c.acceptedAt, today).toLowerCase()}`);
    return parts[0] || "No uploads in the last 90 days";
  }
  if (c.portal === "invited")
    return c.invitedAt
      ? `Invite sent ${agoText(c.invitedAt, today).toLowerCase()}`
      : "Invite sent";
  if (c.portal === "expired")
    return "Invite expired · resend from the client's Client portal tab";
  return "—";
};

/** Portal clients ordered like the mock-up: active, invited, expired, not invited. */
export const PORTAL_ORDER = {
  active: 0,
  invited: 1,
  expired: 2,
  none: 3,
} as const;
